import { useState } from "react";
import api, { fmtMoney, fmtDate, errDetail } from "../lib/api";
import CrudPage from "../components/CrudPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Wallet } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const [txnRow, setTxnRow] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ type: "tahsilat", amount: "", method: "nakit", note: "" });
  const [reloadFn, setReloadFn] = useState(null);
  const [saving, setSaving] = useState(false);

  const openTxn = async (row, reload) => {
    setTxnRow(row);
    setReloadFn(() => reload);
    setForm({ type: "tahsilat", amount: "", method: "nakit", note: "" });
    try {
      const { data } = await api.get(`/customers/${row.id}/transactions`);
      setHistory(data.items);
    } catch (e) { setHistory([]); }
  };

  const saveTxn = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Geçerli bir tutar girin"); return; }
    setSaving(true);
    try {
      await api.post(`/customers/${txnRow.id}/transactions`, { ...form, amount: Number(form.amount) });
      toast.success(form.type === "tahsilat" ? "Tahsilat kaydedildi" : "Veresiye kaydedildi");
      setTxnRow(null);
      reloadFn && reloadFn();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <CrudPage
        title="Müşteriler"
        subtitle="Müşteri kartları, cari bakiye ve tahsilatlar"
        module="customers"
        endpoint="/customers"
        addLabel="Yeni Müşteri"
        defaultValues={{ balance: 0, total_purchases: 0 }}
        columns={[
          { key: "name", label: "Ad Soyad", render: (r) => <span className="font-medium">{r.name}</span> },
          { key: "phone", label: "Telefon" },
          { key: "address", label: "Adres" },
          { key: "total_purchases", label: "Toplam Alışveriş", render: (r) => fmtMoney(r.total_purchases || 0) },
          {
            key: "balance", label: "Cari Bakiye",
            render: (r) => (
              <span className={`font-semibold ${(r.balance || 0) > 0 ? "text-red-500" : "text-emerald-600"}`}>
                {fmtMoney(r.balance || 0)}
              </span>
            ),
          },
          { key: "notes", label: "Notlar" },
        ]}
        fields={[
          { name: "name", label: "Ad Soyad", required: true },
          { name: "phone", label: "Telefon" },
          { name: "address", label: "Adres", colSpan: 2 },
          { name: "notes", label: "Notlar", type: "textarea" },
        ]}
        rowActions={(row, reload) => (
          <button data-testid={`customer-txn-${row.id}`} onClick={() => openTxn(row, reload)}
            title="Cari İşlem"
            className="flex h-8 w-8 items-center justify-center border border-gray-200 text-[#D4AF37] transition-colors duration-200 hover:border-[#D4AF37]">
            <Wallet size={14} />
          </button>
        )}
      />

      <Dialog open={Boolean(txnRow)} onOpenChange={(o) => !o && setTxnRow(null)}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Cari İşlem · {txnRow?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Mevcut Bakiye: <span className={`font-semibold ${(txnRow?.balance || 0) > 0 ? "text-red-500" : "text-emerald-600"}`}>{fmtMoney(txnRow?.balance || 0)}</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">İşlem Türü</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="customer-txn-type" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tahsilat">Tahsilat</SelectItem>
                    <SelectItem value="veresiye">Veresiye</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Tutar (₺)</Label>
                <Input data-testid="customer-txn-amount" type="number" className="rounded-none" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
            </div>
            {form.type === "tahsilat" && (
              <div>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Ödeme Yöntemi</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nakit">Nakit</SelectItem>
                    <SelectItem value="kredi_karti">Kredi Kartı</SelectItem>
                    <SelectItem value="havale">Havale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Not</Label>
              <Input className="rounded-none" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <div className="flex justify-end">
              <Button data-testid="customer-txn-save" disabled={saving} onClick={saveTxn}
                className="rounded-none bg-[#0B1F3A] text-white hover:bg-[#132c4f]">Kaydet</Button>
            </div>
            {history.length > 0 && (
              <div className="border-t border-gray-200 pt-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Geçmiş İşlemler</p>
                <div className="max-h-40 space-y-2 overflow-y-auto">
                  {history.map((h) => (
                    <div key={h.id} className="flex justify-between text-sm">
                      <span className={h.type === "tahsilat" ? "text-emerald-600" : "text-red-500"}>
                        {h.type === "tahsilat" ? "Tahsilat" : "Veresiye"}
                      </span>
                      <span>{fmtMoney(h.amount)} · {fmtDate(h.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
